import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon, PlayCircleIcon, SparklesIcon, LightBulbIcon, ArrowTrendingUpIcon, EyeIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const HeroPage = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const heroImages = [
        "https://images.unsplash.com/photo-1469334031218-e382a71b716b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80", // Moved up
        "https://images.unsplash.com/photo-1469334031218-e382a71b716b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
        "https://images.unsplash.com/photo-1445205170230-053b83016050?ixlib=rb-4.0.3&auto=format&fit=crop&w=1171&q=80",
        "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80"
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="bg-white overflow-hidden">
            {/* Navigation */}
            <header className="absolute inset-x-0 top-0 z-50">
                <nav className="flex items-center justify-between p-6 lg:px-8" aria-label="Global">
                    <div className="flex lg:flex-1">
                        <Link to="/" className="-m-1.5 p-1.5">
                            <span className="sr-only">Fashion Buyer</span>
                            <img
                                className="h-8 w-auto"
                                src="/logo2.svg"
                                alt="Fashion Buyer Logo"
                            />
                        </Link>
                    </div>
                    <div className="hidden lg:flex lg:gap-x-8">
                        <a href="#trends" className="relative px-4 py-2 text-base font-semibold leading-6 text-gray-800 hover:text-blue-600 transition-all duration-300 group">
                            <span className="relative z-10">Latest Trends</span>
                            <div className="absolute inset-0 bg-blue-50 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out"></div>
                        </a>
                        <a href="#about" className="relative px-4 py-2 text-base font-semibold leading-6 text-gray-800 hover:text-blue-600 transition-all duration-300 group">
                            <span className="relative z-10">About Us</span>
                            <div className="absolute inset-0 bg-blue-50 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out"></div>
                        </a>
                        <a href="#smarter-buying" className="relative px-4 py-2 text-base font-semibold leading-6 text-gray-800 hover:text-blue-600 transition-all duration-300 group">
                            <span className="relative z-10">Smarter Buying</span>
                            <div className="absolute inset-0 bg-blue-50 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out"></div>
                        </a>
                    </div>
                    <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                        <Link to="/login" className="relative px-4 py-2 text-base font-semibold leading-6 text-gray-800 hover:text-blue-600 transition-all duration-300 group">
                            <span className="relative z-10">Log in <span aria-hidden="true">&rarr;</span></span>
                            <div className="absolute inset-0 bg-blue-50 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 ease-out"></div>
                        </Link>
                    </div>
                </nav>
            </header>

            {/* Hero Section */}
            <div className="relative">
                <div className="mx-auto max-w-7xl">
                    <div className="relative z-10 pt-14 lg:w-full lg:max-w-2xl">
                        <svg
                            className="absolute inset-y-0 right-8 hidden h-full w-80 translate-x-1/2 transform fill-white lg:block"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        >
                            <polygon points="0,0 90,0 50,100 0,100" />
                        </svg>

                        <div className="relative px-6 py-32 sm:py-40 lg:px-8 lg:py-56 lg:pr-0">
                            <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl">
                                <div className="hidden sm:mb-10 sm:flex">
                                    <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-500 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                        Discover the latest fashion trends for this season.{' '}
                                        <Link to="/trends" className="whitespace-nowrap font-semibold text-blue-600">
                                            <span className="absolute inset-0" aria-hidden="true" />
                                            Read more <span aria-hidden="true">&rarr;</span>
                                        </Link>
                                    </div>
                                </div>
                                <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                                    Elevate Your Fashion Buying Experience
                                </h1>
                                <p className="mt-6 text-lg leading-8 text-gray-600">
                                    Make smarter buying decisions with AI-powered trend analysis, curated collections, and personalized recommendations tailored to your brand&apos;s unique style and customer base.
                                </p>
                                <div className="mt-10 flex items-center gap-x-6">
                                    <Link
                                        to="/register"
                                        className="rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                    >
                                        Get started
                                    </Link>
                                    <Link to="/demo" className="text-sm font-semibold leading-6 text-gray-900 flex items-center">
                                        Watch demo <ArrowRightIcon className="h-4 w-4 ml-1" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
                </div>
            </div>
            {/* Latest Trends Section */}
            <div id="trends" className="bg-white py-16 sm:py-24 lg:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:text-center">
                        <h2 className="text-base font-semibold leading-7 text-blue-600">Latest Trends</h2>
                        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Stay Ahead of Fashion Trends
                        </p>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Discover the hottest fashion trends for 2025. Our AI-powered trend analysis keeps you informed about what&apos;s trending in the fashion world.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                    <ArrowTrendingUpIcon className="h-5 w-5 flex-none text-blue-600" aria-hidden="true" />
                                    Sustainable Fashion
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                    <p className="flex-auto">Eco-friendly materials and sustainable production methods are becoming increasingly popular among consumers and brands alike.</p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                    <SparklesIcon className="h-5 w-5 flex-none text-blue-600" aria-hidden="true" />
                                    Tech-Integrated Apparel
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                    <p className="flex-auto">Smart fabrics and wearable technology are revolutionizing how we think about clothing functionality and style.</p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                    <EyeIcon className="h-5 w-5 flex-none text-blue-600" aria-hidden="true" />
                                    Minimalist Aesthetics
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                    <p className="flex-auto">Clean lines, neutral colors, and versatile pieces that can be mixed and matched are dominating fashion collections.</p>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>

            {/* Smarter Buying Section */}
            <div id="smarter-buying" className="bg-white py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:text-center">
                        <h2 className="text-base font-semibold leading-7 text-blue-600">
                            Smarter Buying
                        </h2>
                        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Everything you need to make informed fashion buying decisions
                        </p>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Our platform combines AI-powered trend analysis with expert curation to help you stay ahead of the fashion curve and make confident buying decisions.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
                            <div className="relative pl-16">
                                <dt className="text-base font-semibold leading-7 text-gray-900">
                                    <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                                        </svg>
                                    </div>
                                    Trend Analysis
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">
                                    Stay ahead of the curve with AI-powered trend forecasting that analyzes global runway shows, social media, and consumer behavior.
                                </dd>
                            </div>
                            <div className="relative pl-16">
                                <dt className="text-base font-semibold leading-7 text-gray-900">
                                    <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                        </svg>
                                    </div>
                                    Curated Collections
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">
                                    Explore expertly curated collections that match your brand's aesthetic and customer preferences, saving you time and effort.
                                </dd>
                            </div>
                            <div className="relative pl-16">
                                <dt className="text-base font-semibold leading-7 text-gray-900">
                                    <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
                                        </svg>
                                    </div>
                                    Performance Analytics
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">
                                    Track the performance of your purchases with detailed analytics that help you understand what resonates with your customers.
                                </dd>
                            </div>
                            <div className="relative pl-16">
                                <dt className="text-base font-semibold leading-7 text-gray-900">
                                    <div className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                        </svg>
                                    </div>
                                    Personalized Recommendations
                                </dt>
                                <dd className="mt-2 text-base leading-7 text-gray-600">
                                    Receive tailored recommendations based on your previous selections, customer data, and current market trends.
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>

            <div id="about" className="bg-gray-100 py-16 sm:py-24 lg:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8 lg:flex lg:items-center lg:justify-between">
                    <div className="lg:w-1/2">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            About Us
                        </h2>
                        <p className="mt-4 text-lg leading-6 text-gray-600">
                            At Suanfamama, we leverage AI to empower fashion buyers with the tools they need to make smarter, more informed decisions. Dedicated to innovation and quality, our fashion buyer agent provides trend analysis, curated collections, and personalized recommendations tailored to your brand&apos;s unique style and audience.
                        </p>
                    </div>
                </div>
            </div>
            <div className="bg-blue-600">
                <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:flex lg:items-center lg:justify-between lg:px-8">
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Ready to transform your buying process?
                        <br />
                        Start using our platform today.
                    </h2>
                    <div className="mt-10 flex items-center gap-x-6 lg:mt-0 lg:flex-shrink-0">
                        <Link
                            to="/register"
                            className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-blue-600 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                        >
                            Get started
                        </Link>
                        <a href="mailto:wei@suanfamama.com" className="text-sm font-semibold leading-6 text-white">
                            Contact sales <span aria-hidden="true">&rarr;</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-white">
                <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-center lg:px-8">
                    <div className="mt-8 md:mt-0">
                        <p className="text-center text-xs leading-5 text-gray-500">
                            &copy; 2025 Suanfamama, Inc. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HeroPage;